import { db as defaultDb, type MezDb, type OutboxItem } from './db'

/** Throw from a handler when retrying can never succeed (e.g. validation or permission errors). */
export class PermanentError extends Error {
  constructor(message: string) {
    super(message)
    this.name = 'PermanentError'
  }
}

export type OutboxHandler = (item: OutboxItem) => Promise<void>

const handlers = new Map<string, OutboxHandler>()

export function registerOutboxHandler(type: string, handler: OutboxHandler) {
  handlers.set(type, handler)
}

const BASE_DELAY_MS = 5_000
const MAX_DELAY_MS = 5 * 60_000

/** 5s, 10s, 20s ... capped at 5 minutes. */
export function backoffMs(attempts: number) {
  return Math.min(MAX_DELAY_MS, BASE_DELAY_MS * 2 ** Math.max(0, attempts - 1))
}

// --- flushing state, observable from React -----------------------------------------------------

type Listener = () => void
const listeners = new Set<Listener>()
let flushing = false

function setFlushing(value: boolean) {
  flushing = value
  for (const l of listeners) l()
}

const sentListeners = new Set<(item: OutboxItem) => void>()

/** Called after each item reaches the server (used to refresh lists). */
export function onOutboxSent(listener: (item: OutboxItem) => void) {
  sentListeners.add(listener)
  return () => {
    sentListeners.delete(listener)
  }
}

export const flushState = {
  subscribe(listener: Listener) {
    listeners.add(listener)
    return () => listeners.delete(listener)
  },
  get: () => flushing,
}

// --- API ----------------------------------------------------------------------------------------

export type EnqueueInput = {
  type: string
  payload: unknown
  label: string
  userId: string
  id?: string
}

export async function enqueue(input: EnqueueInput, database: MezDb = defaultDb) {
  const item: OutboxItem = {
    id: input.id ?? crypto.randomUUID(),
    type: input.type,
    payload: input.payload,
    label: input.label,
    userId: input.userId,
    createdAt: Date.now(),
    attempts: 0,
    nextAttemptAt: 0,
    status: 'pending',
  }
  await database.outbox.add(item)
  return item
}

export async function retryNow(id?: string, database: MezDb = defaultDb) {
  const items = id ? [await database.outbox.get(id)] : await database.outbox.toArray()
  await database.outbox.bulkPut(
    items
      .filter((i): i is OutboxItem => !!i)
      .map((i) => ({ ...i, status: 'pending' as const, nextAttemptAt: 0 })),
  )
}

export type FlushResult = { sent: number; failed: number; deferred: number }

let running: Promise<FlushResult> | null = null

/**
 * Send everything that is due, oldest first. Items are processed one at a time so an upload
 * always lands before the record that references it. A temporary failure stops the run (later
 * items may depend on it) and schedules a retry with backoff; a PermanentError parks that item as
 * "failed" for the user to see, and the run continues.
 */
export function flushOutbox(
  options: { userId?: string; now?: () => number; database?: MezDb; isOnline?: () => boolean } = {},
): Promise<FlushResult> {
  if (running) return running
  running = doFlush(options).finally(() => {
    running = null
    setFlushing(false)
  })
  return running
}

async function doFlush({
  userId,
  now = Date.now,
  database = defaultDb,
  isOnline = () => (typeof navigator === 'undefined' ? true : navigator.onLine),
}: {
  userId?: string
  now?: () => number
  database?: MezDb
  isOnline?: () => boolean
}): Promise<FlushResult> {
  const result: FlushResult = { sent: 0, failed: 0, deferred: 0 }
  if (!isOnline()) return result

  // An item left "sending" means the app closed mid-send. The server dedupes on id, so resend.
  await database.outbox.where('status').equals('sending').modify({ status: 'pending' })

  const due = (await database.outbox.orderBy('createdAt').toArray()).filter(
    (i) => i.status === 'pending' && (!userId || i.userId === userId),
  )
  if (due.length === 0) return result
  setFlushing(true)

  for (const item of due) {
    if (item.nextAttemptAt > now()) {
      // Not due yet: keep order by not skipping ahead of it.
      result.deferred++
      break
    }
    const handler = handlers.get(item.type)
    if (!handler) {
      await database.outbox.update(item.id, {
        status: 'failed',
        lastError: `No handler for "${item.type}"`,
      })
      result.failed++
      continue
    }
    await database.outbox.update(item.id, { status: 'sending' })
    try {
      await handler(item)
      await database.outbox.delete(item.id)
      result.sent++
      for (const l of sentListeners) l(item)
    } catch (error) {
      const message = error instanceof Error ? error.message : String(error)
      const attempts = item.attempts + 1
      // Rejected on purpose by the server (permission, validation): retrying can never work.
      if (
        error instanceof PermanentError ||
        (error as { permanent?: boolean }).permanent === true
      ) {
        await database.outbox.update(item.id, { status: 'failed', attempts, lastError: message })
        result.failed++
        continue
      }
      await database.outbox.update(item.id, {
        status: 'pending',
        attempts,
        lastError: message,
        nextAttemptAt: now() + backoffMs(attempts),
      })
      result.deferred++
      break
    }
  }
  return result
}

/** For tests. */
export function __resetOutboxHandlers() {
  handlers.clear()
}

/** Remove a parked item. For a Relux the saved draft stays under Not sent yet. */
export async function discardOutboxItem(id: string, database: MezDb = defaultDb) {
  await database.outbox.delete(id)
}
